import React, { FormEvent, useState } from "react";

import AuthLayout from "../components/templates/AuthLayout";
import UserService from "../services/UserService";

type RegisterResponse = {
  data: Record<string, unknown> | null;
  status: "success" | "error";
  code: number;
  errors: string[];
};

export default function RegisterUser() {
  const { create } = UserService();
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formValues, setFormValues] = useState({
    first_name: "",
    last_name: "",
    company: "",
    username: "",
    email: "",
    password: "",
    confirm_pass: "",
  });

  const updateField = <K extends keyof typeof formValues>(
    field: K,
    value: (typeof formValues)[K],
  ) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrors([]);

    if (formValues.password !== formValues.confirm_pass) {
      setErrors(["Passwords do not match."]);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = (await create(formValues)) as RegisterResponse;

      if (response?.code === 200) {
        window.location.href = "/database/index.php?k=signin";
        return;
      }

      if (response?.code >= 400 && response?.code < 600) {
        setErrors(
          response.errors?.length > 0
            ? response.errors
            : ["Unable to create account."],
        );
        return;
      }

      setErrors(["Unable to create account."]);
    } catch (err) {
      setErrors(["Unable to create account."]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    "block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-500 dark:bg-slate-700 dark:text-gray-100 dark:placeholder-gray-500";
  const labelClass =
    "mb-1.5 block text-sm font-medium text-gray-900 dark:text-gray-200";

  return (
    <AuthLayout>
      <h1 className="mb-1 text-2xl font-medium text-gray-900 dark:text-gray-100">
        Sign up
      </h1>
      <p className="mb-7 text-sm text-gray-500 dark:text-gray-400">
        Fill in your details to create a new account.
      </p>

      {errors.length > 0 && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
        >
          {errors.map((err, i) => (
            <p key={err} className={i > 0 ? "mt-1" : ""}>
              {err}
            </p>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid gap-x-3 sm:grid-cols-2">
          <div className="mb-4">
            <label htmlFor="first_name" className={labelClass}>
              First name
            </label>
            <input
              id="first_name"
              name="first_name"
              type="text"
              autoComplete="given-name"
              required
              value={formValues.first_name}
              onChange={(e) => updateField("first_name", e.target.value)}
              placeholder="Jane"
              className={inputClass}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="last_name" className={labelClass}>
              Last name
            </label>
            <input
              id="last_name"
              name="last_name"
              type="text"
              autoComplete="family-name"
              required
              value={formValues.last_name}
              onChange={(e) => updateField("last_name", e.target.value)}
              placeholder="Doe"
              className={inputClass}
            />
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="company" className={labelClass}>
            Company
          </label>
          <input
            id="company"
            name="company"
            type="text"
            autoComplete="organization"
            required
            value={formValues.company}
            onChange={(e) => updateField("company", e.target.value)}
            placeholder="Digar LLC"
            className={inputClass}
          />
        </div>

        <div className="mb-4">
          <label htmlFor="username" className={labelClass}>
            Username
          </label>
          <input
            id="username"
            name="username"
            type="text"
            autoComplete="username"
            required
            value={formValues.username}
            onChange={(e) => updateField("username", e.target.value)}
            placeholder="jdoe"
            className={inputClass}
          />
        </div>

        <div className="mb-4">
          <label htmlFor="email" className={labelClass}>
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={formValues.email}
            onChange={(e) => updateField("email", e.target.value)}
            placeholder="name@example.com"
            className={inputClass}
          />
        </div>

        <div className="mb-4">
          <label htmlFor="password" className={labelClass}>
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              value={formValues.password}
              onChange={(e) => updateField("password", e.target.value)}
              placeholder="••••••••"
              className={`${inputClass} pr-16`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded px-2.5 py-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        <div className="mb-5">
          <label htmlFor="confirm_pass" className={labelClass}>
            Re-enter password
          </label>
          <div className="relative">
            <input
              id="confirm_pass"
              name="confirm_pass"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              value={formValues.confirm_pass}
              onChange={(e) => updateField("confirm_pass", e.target.value)}
              placeholder="••••••••"
              className={`${inputClass} pr-16`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              aria-label={
                showConfirmPassword
                  ? "Hide confirm password"
                  : "Show confirm password"
              }
              aria-pressed={showConfirmPassword}
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded px-2.5 py-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {showConfirmPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="mb-4 flex w-full items-center justify-center rounded-md bg-blue-700 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-blue-400 dark:bg-blue-600 dark:hover:bg-blue-500 dark:focus:ring-offset-slate-800 dark:disabled:bg-blue-900 dark:disabled:text-blue-300"
        >
          {isSubmitting ? (
            <>
              <svg
                className="mr-2 h-4 w-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  opacity="0.25"
                />
                <path
                  d="M4 12a8 8 0 0 1 8-8"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              Creating account...
            </>
          ) : (
            "Sign up"
          )}
        </button>

        <p className="text-center text-sm text-gray-600 dark:text-gray-400">
          Already have an account?{" "}
          <a
            href="/database/index.php"
            className="font-medium text-blue-700 hover:underline dark:text-blue-400"
          >
            Sign in
          </a>
          {" · "}
          <a
            href="/database/forgot.php"
            className="font-medium text-blue-700 hover:underline dark:text-blue-400"
          >
            Forgot password?
          </a>
        </p>
      </form>
    </AuthLayout>
  );
}
